// Wrapped so the file can be included by more than one section/snippet without a
// duplicate top-level class declaration (search-drawer is global via theme.liquid,
// the header search form loads it too). The customElements.define guards below make
// a second execution a no-op.
(() => {
class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.lastTrigger = null;
    this.closeTimer = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-search-drawer-panel]');
    this.searchInput = this.querySelector('[data-search-input]');

    this.abortController?.abort();
    this.abortController = new AbortController();

    this.addEventListener('click', this.handleClick, { signal: this.abortController.signal });
    document.addEventListener('keydown', this.handleKeydown, { signal: this.abortController.signal });
    document.addEventListener('click', this.handleTriggerClick, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    window.clearTimeout(this.closeTimer);
    this.abortController?.abort();
    document.body.classList.remove('search-drawer-open');
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('[data-search-drawer-open]');
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger);
  }

  handleClick(event) {
    const closeTarget = event.target.closest('[data-search-drawer-close]');
    if (closeTarget) this.close();
  }

  handleKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    this.trapFocus(event);
  }

  trapFocus(event) {
    const focusableElements = [
      ...this.querySelectorAll(
        ['a[href]:not([tabindex="-1"])', 'button:not([disabled])', 'input:not([disabled])', '[tabindex]:not([tabindex="-1"])'].join(','),
      ),
    ].filter((element) => !element.hidden && element.offsetParent !== null);

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  open(trigger = null, options = {}) {
    const { focusInput = true } = options;

    window.clearTimeout(this.closeTimer);

    if (trigger) {
      this.lastTrigger = trigger;
      this.lastTrigger.setAttribute('aria-expanded', 'true');
    }

    if (this.isOpen) return;

    this.isOpen = true;
    this.hidden = false;
    document.body.classList.add('search-drawer-open');

    window.requestAnimationFrame(() => {
      this.classList.add('is-open');
      if (focusInput) this.searchInput?.focus({ preventScroll: true });
    });
  }

  /*
   * Opened because the merchant selected the section in the theme editor, not
   * because a visitor clicked a trigger. No trigger is recorded — so closing it
   * again hands focus nowhere — and focus stays in the editor's own panel
   * instead of being pulled into the preview iframe mid-edit.
   */
  openFromEditor() {
    this.open(null, { focusInput: false });
  }

  closeFromEditor() {
    if (!this.isOpen) return;
    this.close();
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    document.body.classList.remove('search-drawer-open');

    if (this.lastTrigger) {
      this.lastTrigger.setAttribute('aria-expanded', 'false');
    }

    this.closeTimer = window.setTimeout(() => {
      if (this.isOpen) return;

      this.hidden = true;
      this.querySelector('search-modal')?.reset();

      if (this.lastTrigger && document.contains(this.lastTrigger)) {
        this.lastTrigger.focus({ preventScroll: true });
      }

      this.lastTrigger = null;
    }, 300);
  }
}

class SearchModal extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.recentController = null;
    this.debounceTimer = null;
    this.DEBOUNCE_DELAY = 300;
  }

  get predictiveSearchUrl() {
    return '/search/suggest.json';
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.searchInput = this.querySelector('[data-search-input]');
    this.searchForm = this.querySelector('[data-search-form]');
    this.resultsContainer = this.querySelector('[data-search-results]');
    this.loadingState = this.querySelector('[data-search-loading]');
    this.defaultState = this.querySelector('[data-search-default]');
    this.recentSection = this.querySelector('[data-search-recent]');
    this.recentGrid = this.querySelector('[data-search-recent-grid]');
    this.actionButton = this.querySelector('[data-search-action]');
    this.actionIconSearch = this.querySelector('[data-search-action-icon-search]');
    this.actionIconClear = this.querySelector('[data-search-action-icon-clear]');
    this.statusRegion = this.querySelector('[data-search-status]');
    this.contentContainer = this.querySelector('[data-search-content]');

    this.labels = {
      products: this.dataset.labelProducts || 'Products',
      articles: this.dataset.labelArticles || 'Articles',
      pages: this.dataset.labelPages || 'Pages',
      soldOut: this.dataset.labelSoldOut || 'Sold out',
      noResults: this.dataset.labelNoResults || 'No results found',
      error: this.dataset.labelError || 'An error occurred. Please try again.',
      search: this.dataset.labelSearch || 'Search',
      clear: this.dataset.labelClear || 'Clear search',
      searching: this.dataset.labelSearching || 'Searching…',
      resultsAnnouncement: this.dataset.labelResultsAnnouncement || '{{ count }} result(s) found',
    };

    // Same section as assets/product-related-recently-viewed.js's own card-fetch-by-handle
    // logic — reusing the exact same section id and fetch/fallback strategy that section
    // already relies on (see fetchProductCard below).
    this.cardSectionId = this.dataset.cardSectionId || 'related_recently_viewed';
    this.productRoot = this.dataset.productRoot || '/products/';
    this.searchUrl = this.dataset.searchUrl || '/search';
    // A live-search carousel only ever shows a handful of cards at once — capping the
    // per-product card fetch keeps the worst case (each card can need 2 requests, see
    // fetchProductCard) from growing unbounded as suggest.json returns more matches.
    this.productResultsLimit = 6;

    this.recentStorageKey = this.dataset.recentStorageKey || 'amerce_recently_viewed_products';
    this.recentLimit = Math.max(1, Number(this.dataset.recentLimit) || 4);

    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', (e) => this.handleSearchInput(e));
    this.searchForm?.addEventListener('submit', (e) => this.handleSubmit(e));
    this.actionButton?.addEventListener('click', () => this.handleActionClick());
    this.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.addEventListener('search-scope:change', () => this.handleScopeChange());

    this.loadRecentlyViewed();
  }

  // --- Category scope ---
  // Storefront search has no collection parameter, and filter.p.* only works once the
  // merchant has created that filter in Search & Discovery. The one narrowing that
  // works everywhere with no setup is the search query syntax itself:
  // `product_type:Accessories AND boots`. Verified against both /search/suggest.json
  // and the /search results page.

  get scopeValue() {
    return this.querySelector('[data-search-scope-input]')?.value.trim() || '';
  }

  buildScopedQuery(query) {
    const scope = this.scopeValue;
    return scope ? `product_type:${scope} AND ${query}` : query;
  }

  // Re-runs the current query when the category changes, so the visible results always
  // match the selected category instead of going stale.
  handleScopeChange() {
    const query = this.searchInput?.value.trim();

    if (!query) return;

    window.clearTimeout(this.debounceTimer);
    this.fetchPredictiveSearch(query);
  }

  handleSearchInput(event) {
    const query = event.target.value.trim();

    this.updateActionButton(query.length > 0);

    window.clearTimeout(this.debounceTimer);

    if (!query) {
      // Without this abort an already in-flight request would come back and render
      // results for a query the shopper has just cleared, spinner included.
      this.abortController?.abort();
      this.setLoading(false);
      this.clearResults();
      return;
    }

    this.debounceTimer = window.setTimeout(() => {
      this.fetchPredictiveSearch(query);
    }, this.DEBOUNCE_DELAY);
  }

  // Swaps the action button between "search" (magnifying glass) and "clear" (X)
  // depending on whether the input has text.
  updateActionButton(hasValue) {
    if (!this.actionButton) return;

    if (this.actionIconSearch) this.actionIconSearch.hidden = hasValue;
    if (this.actionIconClear) this.actionIconClear.hidden = !hasValue;
    this.actionButton.setAttribute('aria-label', hasValue ? this.labels.clear : this.labels.search);
  }

  handleActionClick() {
    if (!this.searchInput) return;

    if (this.searchInput.value.trim()) {
      this.searchInput.value = '';
      this.clearResults();
      this.searchInput.focus();
    } else {
      this.searchForm?.requestSubmit();
    }
  }

  // Called by <search-drawer> when the popup closes, so reopening it always
  // starts from a clean state instead of the previous query/results.
  reset() {
    window.clearTimeout(this.debounceTimer);
    this.abortController?.abort();

    if (this.searchInput) this.searchInput.value = '';
    this.clearResults();

    this.querySelector('search-collection-select')?.reset();
  }

  async fetchPredictiveSearch(query) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    // Deliberately not switching to the results state yet: whatever is on screen —
    // recently viewed on the first search, the previous results on later ones — stays
    // put and dims under the spinner, so the popup keeps its height.
    this.setLoading(true);
    this.announce(this.labels.searching);

    try {
      const response = await fetch(`${this.predictiveSearchUrl}?${this.buildSuggestParams(query)}`, {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Search failed with status ${response.status}`);

      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      const articles = data?.resources?.results?.articles || [];
      const pages = data?.resources?.results?.pages || [];

      // suggest.json only returns compact fields (no rating/swatches), so the real
      // card-product markup for each matched product is fetched by handle — see
      // fetchProductCard below (shared with the recently-viewed carousel). Capped to
      // productResultsLimit: this is a compact carousel preview, not the full results
      // page, so there's no value in fetching more cards than it will ever show.
      const productCards = await this.fetchProductCards(products.slice(0, this.productResultsLimit), signal);
      if (signal.aborted) return;

      // suggest.json found matches, but every single per-product card fetch failed —
      // that's a fetch/markup problem, not "no results". Surface it as an error
      // instead of silently rendering the "no results" state (which would hide the
      // real bug). Details are already logged per-handle in fetchProductCards.
      if (products.length > 0 && productCards.length === 0) {
        console.error(
          `Search found ${products.length} product match(es) for "${query}" but every card fetch failed — see warnings above.`,
        );
        this.renderError();
        this.announce(this.labels.error);
        return;
      }

      this.renderResults(productCards, articles, pages);
      const count = productCards.length + articles.length + pages.length;
      this.announce(count > 0 ? this.labels.resultsAnnouncement.replace('{{ count }}', count) : this.labels.noResults);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
        this.renderError();
        this.announce(this.labels.error);
      }
    } finally {
      if (!signal.aborted) this.setLoading(false);
    }
  }

  // Screen-reader-only status updates for the async search — sighted users see the
  // loading spinner / results directly, but nothing here is otherwise announced.
  announce(message) {
    if (!this.statusRegion) return;
    this.statusRegion.textContent = '';
    window.requestAnimationFrame(() => {
      this.statusRegion.textContent = message;
    });
  }

  async fetchProductCards(products, signal) {
    const handles = products.map((product) => this.getProductHandle(product)).filter(Boolean);
    if (!handles.length) return [];

    const settled = await Promise.allSettled(
      handles.map((handle) => this.fetchProductCard(handle, signal)),
    );

    return settled
      .filter((result) => {
        if (result.status === 'rejected') {
          if (result.reason?.name !== 'AbortError') console.error('Search product card error:', result.reason);
          return false;
        }
        return Boolean(result.value);
      })
      .map((result) => result.value);
  }

  // suggest.json's documented product fields don't reliably include a bare `handle`
  // in every Shopify version/store — `url` (e.g. "/products/the-complete-snowboard")
  // is the one field guaranteed present, so derive the handle from that instead of
  // trusting `product.handle` directly.
  getProductHandle(product) {
    if (product.handle) return product.handle;

    try {
      const segments = new URL(product.url, window.location.origin).pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || null;
    } catch (_) {
      return null;
    }
  }

  // Shopify's Predictive Search (Ajax) API expects a single comma-separated
  // `resources[type]` value and a `resources[limit_scope]=each` flag to apply
  // the limit per resource type — repeated `resources[type]` params or made-up
  // `resources[options][x_first]` keys are silently ignored by the endpoint.
  buildSuggestParams(query) {
    const params = new URLSearchParams();
    params.set('q', this.buildScopedQuery(query));
    params.set('resources[type]', 'product,article,page');
    params.set('resources[limit]', '8');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    return params.toString();
  }

  showResultsState() {
    if (this.defaultState) this.defaultState.hidden = true;
    if (this.resultsContainer) this.resultsContainer.hidden = false;
  }

  showDefaultState() {
    if (this.defaultState) this.defaultState.hidden = false;
    if (this.resultsContainer) this.resultsContainer.hidden = true;
  }

  renderResults(productCards, articles, pages) {
    if (!this.resultsContainer) return;

    // Swap over only now that there is something to swap in.
    this.showResultsState();
    this.resultsContainer.innerHTML = '';

    if (productCards.length === 0 && articles.length === 0 && pages.length === 0) {
      this.resultsContainer.innerHTML = `<div class="search-no-results"><p>${this.escapeHtml(this.labels.noResults)}</p></div>`;
      return;
    }

    if (productCards.length > 0) {
      const section = document.createElement('div');
      section.className = 'search-section-products';
      section.innerHTML = `
        <h3 class="search-section-title">${this.escapeHtml(this.labels.products)}</h3>
        ${this.buildCarouselMarkup(productCards.length)}
      `;
      section.querySelector('.swiper-wrapper').append(...productCards);
      this.resultsContainer.append(section);
    }

    if (articles.length > 0) {
      const section = document.createElement('div');
      section.className = 'search-section-articles';
      section.innerHTML = `
        <h3 class="search-section-title">${this.escapeHtml(this.labels.articles)}</h3>
        <div class="search-results-list">${articles.map((article) => this.createArticleResultHTML(article)).join('')}</div>
      `;
      this.resultsContainer.append(section);
    }

    if (pages.length > 0) {
      const section = document.createElement('div');
      section.className = 'search-section-pages';
      section.innerHTML = `
        <h3 class="search-section-title">${this.escapeHtml(this.labels.pages)}</h3>
        <div class="search-results-list">${pages.map((page) => this.createPageResultHTML(page)).join('')}</div>
      `;
      this.resultsContainer.append(section);
    }
  }

  // tf-swiper (assets/global.js) reads its config straight off this element's
  // dataset — see the shared carousel markup convention documented in CLAUDE.md.
  buildCarouselMarkup(cardCount) {
    return `
      <tf-swiper class="search-card-carousel">
        <div
          class="swiper tf-swiper swiper-init"
          data-preview="4"
          data-tablet="3"
          data-mobile-sm="2"
          data-mobile="2"
          data-space-lg="28"
          data-space-md="20"
          data-space="12"
          data-pagination-lg="${cardCount > 4}"
          data-pagination-md="${cardCount > 3}"
          data-pagination="${cardCount > 2}"
        >
          <ul class="swiper-wrapper" role="list"></ul>
          <div class="swiper-pagination"></div>
        </div>
      </tf-swiper>
    `;
  }

  createArticleResultHTML(article) {
    return `
      <a href="${article.url}" class="search-result-item search-article-item">
        <div class="search-result-content">
          <p class="search-result-title">${this.escapeHtml(article.title)}</p>
          <p class="search-result-type">${this.escapeHtml(this.labels.articles)}</p>
        </div>
      </a>
    `;
  }

  createPageResultHTML(page) {
    return `
      <a href="${page.url}" class="search-result-item search-page-item">
        <div class="search-result-content">
          <p class="search-result-title">${this.escapeHtml(page.title)}</p>
          <p class="search-result-type">${this.escapeHtml(this.labels.pages)}</p>
        </div>
      </a>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  clearResults() {
    if (this.resultsContainer) this.resultsContainer.innerHTML = '';
    this.showDefaultState();
    this.updateActionButton(false);
    this.announce('');
  }

  renderError() {
    if (!this.resultsContainer) return;

    this.showResultsState();
    this.resultsContainer.innerHTML = `<div class="search-error"><p>${this.escapeHtml(this.labels.error)}</p></div>`;
  }

  setLoading(loading) {
    if (this.loadingState) {
      this.loadingState.hidden = !loading;
    }

    // Dims whatever is currently rendered underneath the spinner.
    this.contentContainer?.classList.toggle('is-loading', loading);
  }

  // First Escape clears an active query (and stays open, so typing can resume
  // immediately); only a second Escape — pressed with an already-empty input —
  // is left to bubble up to <search-drawer>'s own Escape handler, which closes
  // the whole popup. Without stopping propagation here, a single Escape would
  // clear the query AND close the drawer in the same keypress.
  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.searchInput) return;

    if (this.searchInput.value) {
      event.stopPropagation();
      this.searchInput.value = '';
      this.clearResults();
      return;
    }

    this.searchInput.blur();
  }

  handleSubmit(event) {
    const query = this.searchInput?.value.trim();

    if (!query) {
      event.preventDefault();
      return;
    }

    const scopedQuery = this.buildScopedQuery(query);

    // With no category selected the form submits itself — the input is already
    // name="q". A category has to be folded into `q`, which the browser can't do, so
    // navigate manually rather than mutating the visible input's value.
    if (scopedQuery === query) return;

    event.preventDefault();

    const url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('q', scopedQuery);
    window.location.assign(url.pathname + url.search);
  }

  // --- Recently viewed products ---
  // Reuses the same localStorage key as assets/product-related-recently-viewed.js
  // (so both features track the same history). Cards are fetched via fetchProductCard
  // below, shared with the live search results.

  readRecentlyViewedHandles() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.recentStorageKey) || '[]');
      if (!Array.isArray(stored)) return [];

      return stored
        .map((entry) => (typeof entry === 'string' ? entry : entry?.handle))
        .filter((handle) => typeof handle === 'string' && handle.length > 0)
        .slice(0, this.recentLimit);
    } catch (_) {
      return [];
    }
  }

  async loadRecentlyViewed() {
    if (!this.recentSection || !this.recentGrid) return;

    const handles = this.readRecentlyViewedHandles();

    if (!handles.length) {
      this.recentSection.hidden = true;
      return;
    }

    this.recentController?.abort();
    this.recentController = new AbortController();

    const settled = await Promise.allSettled(
      handles.map((handle) => this.fetchProductCard(handle, this.recentController.signal)),
    );

    const validCards = settled
      .filter((result) => {
        if (result.status === 'rejected') {
          if (result.reason?.name !== 'AbortError') console.error('Recently viewed card error:', result.reason);
          return false;
        }
        return Boolean(result.value);
      })
      .map((result) => result.value);

    if (!validCards.length) {
      this.recentSection.hidden = true;
      return;
    }

    this.recentGrid.replaceChildren(...validCards);
    this.recentSection.hidden = false;
  }

  // Fetches one product's real card-product markup by handle. Mirrors
  // assets/product-related-recently-viewed.js's fetchRecentlyViewedCard exactly:
  // try the section-only render first (cheap), and if that doesn't yield the
  // matching <template data-recently-viewed-card-template> for this handle, fall
  // back to fetching the full product page and extracting from that. The
  // section-only response doesn't always include it — this fallback is what
  // makes the already-working recently-viewed tab on the product page reliable,
  // so it's required here too, not optional.
  async fetchProductCard(handle, signal) {
    const productUrl = `${this.productRoot}${encodeURIComponent(handle)}`;

    const sectionUrl = new URL(productUrl, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.cardSectionId);

    let response = await fetch(sectionUrl.toString(), {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (response.ok) {
      const card = this.extractProductCard(await response.text(), handle);
      if (card) return card;
    }

    response = await fetch(new URL(productUrl, window.location.origin).toString(), {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
      console.warn(`Product card fetch failed for "${handle}": HTTP ${response.status}`);
      return null;
    }

    const card = this.extractProductCard(await response.text(), handle);
    if (!card) console.warn(`Product card markup not found for "${handle}" in either fetch.`);
    return card;
  }

  extractProductCard(html, handle) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const templates = Array.from(parsed.querySelectorAll('template[data-recently-viewed-card-template]'));
    const template = templates.find((item) => item.dataset.productHandle === handle) || templates[0];
    const item = template?.content.querySelector('.product-related-recently-viewed__item');
    const card = item?.querySelector('.card-product');
    if (!card) return null;

    const listItem = document.createElement('li');
    listItem.className = 'search-card-item swiper-slide';
    listItem.append(card.cloneNode(true));
    return listItem;
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer);
}

if (!customElements.get('search-modal')) {
  customElements.define('search-modal', SearchModal);
}

/*
 * Theme editor only. The drawer renders `hidden` and its only trigger lives in
 * the header, so selecting the section in the sidebar used to change nothing in
 * the preview — the merchant had to go and find the search icon themselves.
 * Selecting it now opens the drawer, and selecting anything else, or deselecting,
 * closes it again: it is a fixed full-screen overlay, so leaving it open would
 * cover the section the merchant moved on to.
 *
 * Only a drawer this code opened is ever closed again, so one the merchant opened
 * from the header is left alone.
 *
 * `editorOpen` lives out here rather than on the element because the editor
 * replaces the section node on every setting change. The window flag is because
 * search.js runs once per script tag and two tags can be on one page (this
 * section and snippets/form-search-collection.liquid), so the first copy to run
 * claims the hooks and the rest skip them.
 */
if (window.Shopify && Shopify.designMode && !window.amerceSearchDrawerEditorHooks) {
  window.amerceSearchDrawerEditorHooks = true;

  let editorOpen = false;

  const editorEventDrawer = (target) =>
    target instanceof Element ? target.querySelector('search-drawer') : null;

  const closeFromEditor = () => {
    if (!editorOpen) return;
    editorOpen = false;
    document.querySelector('search-drawer')?.closeFromEditor?.();
  };

  document.addEventListener('shopify:section:select', (event) => {
    const drawer = editorEventDrawer(event.target);

    if (!drawer) {
      closeFromEditor();
      return;
    }

    editorOpen = true;
    drawer.openFromEditor?.();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    if (editorEventDrawer(event.target)) closeFromEditor();
  });

  /*
   * A reloaded section connects `hidden`, so a drawer being held open for the
   * merchant is re-opened once the replacement element is in place.
   */
  document.addEventListener('shopify:section:load', (event) => {
    const drawer = editorEventDrawer(event.target);
    if (drawer && editorOpen) drawer.openFromEditor?.();
  });
}
})();
